// components/ui/Badge.jsx
// Created: 2026-07-20
// Project: Giving Words to Voices
// Version: 1.0.0
//
// The Badge primitive — the small status/role pill. The review found ~964
// hardcoded status colours, and that the scheme's warning/danger/info tokens
// had zero consumers: status pills used fixed hex, so they never followed the
// active scheme. A Badge takes a source of colour and renders the pill once.
//
// Three ways to name the colour, in priority order:
//   role="admin"      → a fixed categorical identity hue (ROLE_COLORS)
//   status="danger"   → the ACTIVE scheme's danger/warning/success/info token
//   color="#f59e0b"   → an explicit override, tinted the same way
// A status badge therefore recolours itself when the scheme changes; a role
// badge stays put, because a role's colour is meant to be constant.

import React from 'react';
import { RADIUS, TYPE, WEIGHT, TRACKING, statusVariant, ROLE_COLORS, isDarkTheme } from '../../constants/tokens';
import { SLATE } from '../../constants/palette';
import { Icon } from '../Icon';

const SIZES = {
  sm: { padding: '1px 6px', font: '0.56rem', minH: '18px', icon: 10, gap: '3px' },
  md: { padding: '2px 8px', font: TYPE.xs,   minH: '20px', icon: 12, gap: '4px' },
};

// Resolve the pill's three colours (fill / text / border) from whichever
// source the caller named. Everything normalises to {fill, text, border}.
function resolveColors({ status, role, color, theme, isDark }) {
  if (role) {
    const r = ROLE_COLORS[role] || ROLE_COLORS.viewer;
    return { fill: r.bg, text: r.text, border: r.border };
  }
  const src = color || (status && theme[status]) || theme.textSub || SLATE.muted;
  return statusVariant(src, isDark);
}

export default function Badge({
  children,
  status,               // 'success' | 'warning' | 'danger' | 'info' — a scheme token
  role,                 // 'admin' | 'editor' | 'reviewer' | 'viewer'
  color,                // explicit override hex
  size = 'md',
  icon = null,
  uppercase = true,
  style = {},
  ...rest
}) {
  // theme is passed by prop everywhere in the app (no ThemeContext yet).
  const theme = rest.theme || {};
  delete rest.theme;

  const isDark = isDarkTheme(theme);
  const sz = SIZES[size] || SIZES.md;
  const c = resolveColors({ status, role, color, theme, isDark });

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: sz.gap,
        minHeight: sz.minH,
        padding: sz.padding,
        borderRadius: RADIUS.pill,
        background: c.fill,
        border: `1px solid ${c.border}`,
        color: c.text,
        fontSize: sz.font,
        fontWeight: WEIGHT.bold,
        textTransform: uppercase ? 'uppercase' : 'none',
        letterSpacing: TRACKING.label,
        whiteSpace: 'nowrap',
        ...style,
      }}
      {...rest}
    >
      {icon && <Icon icon={icon} size={sz.icon} color={c.text} />}
      {children}
    </span>
  );
}
